/** One vendor: every service level, both figures, and what the difference is worth. */

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AtRiskPanel } from "../components/AtRiskPanel";
import { Breadcrumb, Notice, Section, StatTile } from "../components/Chrome";
import {
  ProvenanceBadge,
  SeverityBadge,
  SourceBadge,
  TargetBadge,
  TierBadge,
  VerdictBadge,
} from "../components/Badges";
import { EscalationDraft } from "../components/EscalationDraft";
import {
  creditDirectionLabel,
  metricValue,
  money,
  recordCount,
  shortPeriodLabel,
  signedGap,
} from "../lib/format";
import type { Vendor } from "../lib/types";

function CompositeTrend({ vendor }: { vendor: Vendor }) {
  const data = vendor.history.map((point) => ({
    period: shortPeriodLabel(point.period),
    Reported: point.reported,
    Computed: point.computed,
  }));

  return (
    <div className="card" style={{ padding: 16, height: 240 }}>
      <div style={{ fontSize: 11, color: "var(--slate)", letterSpacing: "0.06em", marginBottom: 8 }}>
        COMPOSITE, {vendor.history.length} MONTHS
      </div>
      <ResponsiveContainer width="100%" height="88%">
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
          <CartesianGrid stroke="rgba(239,233,221,0.08)" vertical={false} />
          <XAxis dataKey="period" stroke="var(--slate)" fontSize={11} tickLine={false} />
          <YAxis stroke="var(--slate)" fontSize={11} tickLine={false} domain={["dataMin - 3", 100]} />
          <Tooltip
            contentStyle={{
              background: "var(--ink)",
              border: "1px solid var(--line-strong)",
              borderRadius: 6,
              fontSize: 12,
            }}
          />
          <Line type="monotone" dataKey="Reported" stroke="var(--slate)" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Computed" stroke="var(--gold)" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function VendorView({
  vendor,
  onBack,
  onOpenServiceLevel,
}: {
  vendor: Vendor;
  onBack: () => void;
  onOpenServiceLevel: (serviceLevelId: string) => void;
}) {
  const verified = vendor.service_levels.filter((level) => level.claimable);
  const attested = vendor.service_levels.filter((level) => !level.claimable);
  const findingFor = (id: string) => vendor.findings.find((f) => f.service_level_id === id);

  return (
    <div className="fade-in">
      <Breadcrumb items={[{ label: "Portfolio", onClick: onBack }, { label: vendor.name }]} />

      <Section
        title={vendor.name}
        subtitle={vendor.tier_rationale}
        actions={<TierBadge tier={vendor.tier} />}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <StatTile label="Annual spend" value={money(vendor.annual_spend)} />
          <StatTile
            label="Credits computed"
            value={money(vendor.credits.computed)}
            note={creditDirectionLabel(vendor.credits.direction)}
            tone="gold"
          />
          <StatTile
            label="Vendor concedes"
            value={money(vendor.credits.reported)}
            note={`Difference ${money(Math.abs(vendor.credits.difference))}`}
          />
          <StatTile
            label="Records ingested"
            value={vendor.records_ingested.toLocaleString()}
            note={`Contract ${vendor.contract.credit_model} · ${
              vendor.contract.at_risk_amount
                ? `${money(vendor.contract.at_risk_amount)} at risk/mo`
                : `${money(vendor.contract.credit_pool ?? 0)} pool/mo`
            }`}
          />
        </div>
        <CompositeTrend vendor={vendor} />
      </Section>

      <AtRiskPanel items={vendor.at_risk} openPeriod={vendor.open_period} />

      <Section
        title="Independently measured"
        subtitle="These service levels have a source the client controls, so both figures exist and the difference between them is a finding."
      >
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Service level</th>
                <th>Target</th>
                <th style={{ textAlign: "right" }}>Vendor reported</th>
                <th style={{ textAlign: "right" }}>Computed</th>
                <th style={{ textAlign: "right" }}>Gap</th>
                <th>Against target</th>
                <th style={{ textAlign: "right" }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {verified.map((level) => {
                const finding = findingFor(level.id);
                return (
                  <tr
                    key={level.id}
                    className="row"
                    onClick={() => onOpenServiceLevel(level.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="serif" style={{ fontSize: 14.5 }}>
                        {level.name}
                      </div>
                      <div style={{ marginTop: 5, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <ProvenanceBadge provenance={level.provenance} />
                        <SourceBadge source={level.source} />
                        {finding && !level.computed?.evidence_gap && (
                          <SeverityBadge severity={finding.severity} />
                        )}
                      </div>
                    </td>
                    <td style={{ color: "var(--slate)" }}>
                      {level.direction === "gte" ? "≥" : "≤"} {level.expected_target}
                      {level.unit}
                      {level.minimum_target !== null && (
                        <div style={{ fontSize: 11, opacity: 0.75 }}>
                          min {level.minimum_target}
                          {level.unit}
                        </div>
                      )}
                    </td>
                    <td className="num" style={{ textAlign: "right", color: "var(--slate)" }}>
                      {metricValue(level.reported?.value, level.unit)}
                    </td>
                    <td
                      className="num serif"
                      style={{ textAlign: "right", color: "var(--gold)", fontSize: 15 }}
                    >
                      {/* A gated obligation shows why, not a dash. "We could not measure
                          it" and "the evidence did not cover the window" are different
                          statements, and only the second tells anyone what to do. */}
                      {level.computed?.evidence_gap ? (
                        <div style={{ fontFamily: "var(--sans)" }}>
                          <VerdictBadge verdict="insufficient_evidence" />
                          <div
                            style={{
                              fontSize: 10.5,
                              color: "var(--slate)",
                              marginTop: 4,
                              maxWidth: 190,
                              textAlign: "right",
                            }}
                          >
                            {level.computed.evidence_gap.received_days} of{" "}
                            {level.computed.evidence_gap.expected_days} days received
                          </div>
                        </div>
                      ) : (
                        <>
                          {metricValue(level.computed?.value, level.unit)}
                          {level.computed && (
                            <div
                              style={{
                                fontSize: 10.5,
                                color: "var(--slate)",
                                fontFamily: "var(--sans)",
                              }}
                            >
                              {recordCount(level.computed.record_count)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td
                      className="num"
                      style={{
                        textAlign: "right",
                        color: (finding?.gap ?? 0) > 3 ? "var(--rust)" : "var(--parchment)",
                      }}
                    >
                      {signedGap(finding?.gap)}
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 10.5, color: "var(--slate)" }}>
                          vendor <TargetBadge met={level.reported?.met_expected ?? null} />
                        </span>
                        <span style={{ fontSize: 10.5, color: "var(--slate)" }}>
                          ours <TargetBadge met={level.computed?.met_expected ?? null} />
                        </span>
                      </div>
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {level.chargeable ? (
                        money(finding?.credit_computed ?? 0)
                      ) : (
                        <span
                          style={{ color: "var(--slate)", fontSize: 11.5 }}
                          title="An agreed rule: real, worth raising, and not something an executed document says the supplier owes for."
                        >
                          Not chargeable
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      <EscalationDraft vendor={vendor} />

      <Section
        title="Vendor-attested only"
        subtitle="No independent source exists for these, so the platform reports the vendor's figure and makes no claim about it."
      >
        <Notice>
          Verifying these would require data the platform does not currently receive — roster and
          badge records for attrition, line-level billing detail for invoice accuracy. Until it
          does, these figures are the vendor's account of itself and are excluded from every gap
          and credit calculation on this screen.
        </Notice>
        <div className="card table-scroll" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Service level</th>
                <th>Target</th>
                <th style={{ textAlign: "right" }}>Vendor reported</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {attested.map((level) => (
                <tr key={level.id}>
                  <td>
                    <div className="serif" style={{ fontSize: 14.5 }}>
                      {level.name}
                    </div>
                    <div style={{ marginTop: 5 }}>
                      <ProvenanceBadge provenance={level.provenance} />
                    </div>
                  </td>
                  <td style={{ color: "var(--slate)" }}>
                    {level.direction === "gte" ? "≥" : "≤"} {level.expected_target}
                    {level.unit}
                  </td>
                  <td className="num" style={{ textAlign: "right", color: "var(--slate)" }}>
                    {metricValue(level.reported?.value, level.unit)}
                  </td>
                  <td style={{ color: "var(--slate)", fontSize: 12 }}>{level.our_method}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
